import React, { createContext, useContext, useEffect, useState } from "react";
import { useModal } from "_shared/contexts/ModalContext";
import {
  Panel,
  useDeletePanelMutation,
  UserPanelsDocument,
  useUpdatePanelsMutation,
  useUserPanelsQuery,
} from "_shared/graphql";

const UserPanelsContext = createContext({
  panels: [] as Panel[],
  fetchPanels: () => {},
  updatePanels: async (_updates: { id: string & Partial<Panel> }[]) => {},
  hidePanel: async (_id: string, _hidden?: boolean) => {},
  renamePanel: async (_id: string, _name: string) => {},
  removePanel: async (_id: string) => {},
  loading: true,
});

export const UserPanelsProvider: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const { awaitConfirmation } = useModal();

  const [panels, setPanels] = useState<Panel[]>([]);

  const { data, refetch, loading } = useUserPanelsQuery();
  const [updateUserPanels] = useUpdatePanelsMutation();
  const [deleteUserPanel] = useDeletePanelMutation();

  useEffect(() => {
    if (!data) return;
    setPanels(data.panel as Panel[]);
  }, [data]);

  const updatePanels = async (updates: { id: string & Partial<Panel> }[]) => {
    // update local panel state
    const updatedPanels = panels
      .map((panel) => {
        const update = updates.find(({ id }) => id === panel.id);
        return update ? { ...panel, ...update } : panel;
      })
      .sort((a, b) => a.order - b.order);
    setPanels(updatedPanels);
    await updateUserPanels({
      variables: {
        updates: updates.map(({ id, ...update }) => ({
          where: { id: { _eq: id } },
          _set: update,
        })),
      },
      refetchQueries: [UserPanelsDocument],
    });
  };

  const hidePanel = async (id: string, hidden = true) => {
    await updatePanels([{ id, hidden } as { id: string } & Partial<Panel>]);
  };

  // TODO: redirect if on current tab
  const renamePanel = async (id: string, name: string) => {
    await updatePanels([{ id, name } as { id: string } & Partial<Panel>]);
  };

  const removePanel = async (id: string) => {
    const handleRemovePanel = async () => {
      await deleteUserPanel({
        variables: { id },
        refetchQueries: [UserPanelsDocument],
      });
    };

    awaitConfirmation(
      handleRemovePanel,
      "Are you sure you want to delete this tab?",
    );
  };

  return (
    <UserPanelsContext.Provider
      value={{
        panels,
        fetchPanels: refetch,
        updatePanels,
        hidePanel,
        renamePanel,
        removePanel,
        loading: loading || !panels.length,
      }}
    >
      {children}
    </UserPanelsContext.Provider>
  );
};

export const useUserPanels = () => useContext(UserPanelsContext);
